'use client';

import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

type NativeModalPortalHostValue = {
  acquire: () => () => void;
  container: HTMLDialogElement | null;
};

const NativeModalPortalHostContext =
  createContext<NativeModalPortalHostValue | null>(null);

/**
 * Provides a second native top-layer entry for modal UI opened from inside an
 * existing native dialog. A body portal cannot render above a native dialog,
 * regardless of its z-index.
 */
export function NativeModalPortalHostProvider({
  children,
}: {
  children: ReactNode;
}) {
  const hostRef = useRef<HTMLDialogElement | null>(null);
  const activeLayerCountRef = useRef(0);
  const [container, setContainer] = useState<HTMLDialogElement | null>(null);

  const registerHost = useCallback((node: HTMLDialogElement | null) => {
    hostRef.current = node;
    setContainer(node);
  }, []);

  const acquire = useCallback(() => {
    const host = hostRef.current;
    if (!host || !host.isConnected) {
      return () => undefined;
    }

    activeLayerCountRef.current += 1;
    if (!host.open) {
      host.showModal();
    }

    let released = false;
    return () => {
      if (released) {
        return;
      }
      released = true;
      activeLayerCountRef.current = Math.max(
        0,
        activeLayerCountRef.current - 1
      );
      if (activeLayerCountRef.current === 0 && host.open) {
        host.close();
      }
    };
  }, []);

  useEffect(
    () => () => {
      activeLayerCountRef.current = 0;
      if (hostRef.current?.open) {
        hostRef.current.close();
      }
    },
    []
  );

  const contextValue = useMemo(
    () => ({ acquire, container }),
    [acquire, container]
  );

  return (
    <NativeModalPortalHostContext.Provider value={contextValue}>
      {children}
      <dialog
        ref={registerHost}
        className="fixed inset-0 m-0 h-dvh max-h-none w-screen max-w-none overflow-visible border-0 bg-transparent p-0 text-inherit backdrop:bg-transparent"
        data-native-modal-portal-host=""
        onCancel={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        role="presentation"
      />
    </NativeModalPortalHostContext.Provider>
  );
}

export function useNativeModalPortalContainer() {
  return useContext(NativeModalPortalHostContext)?.container ?? null;
}

/** Mount this inside the portaled modal content so the native host is active
 * for exactly the same lifetime as the visible overlay. */
export function NativeModalPortalActivation() {
  const host = useContext(NativeModalPortalHostContext);

  useEffect(() => host?.acquire(), [host]);

  return null;
}
